"use client";

import { useEffect } from "react";
import Icon from "@/components/ui/AppIcon";
import Link from "next/link";

interface Service {
  id: string;
  icon: string;
  title: string;
  description: string;
  gradient: string;
}

export default function ServicesOverview() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -5% 0px" }
    );

    document.querySelectorAll(".animate-on-scroll").forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const services: Service[] = [
    {
      id: "service_1",
      icon: "FilmIcon",
      title: "Film Production",
      description:
        "Full-service film production from concept to final cut. Professional crew, state-of-the-art equipment, and creative direction.",
      gradient: "from-purple-900/40 to-blue-900/40",
    },
    {
      id: "service_2",
      icon: "SparklesIcon",
      title: "Event Management",
      description:
        "Live event production and management. Corporate events, concerts, award ceremonies, and festival coordination.",
      gradient: "from-emerald-900/40 to-teal-900/40",
    },
    {
      id: "service_3",
      icon: "VideoCameraIcon",
      title: "Post-Production",
      description:
        "Professional editing, color grading, sound design, and visual effects. Transform raw footage into cinematic masterpieces.",
      gradient: "from-orange-900/40 to-red-900/40",
    },
  ];

  return (
    <section className="bg-[#080808] text-foreground py-24 md:py-32 relative border-t border-white/5">
      {/* Decorative Section Number */}
      <div className="absolute top-12 right-6 md:right-12 z-0 opacity-[0.05] font-display font-bold text-[8rem] md:text-[10rem] leading-none text-white pointer-events-none select-none tracking-tighter">
        ACT II
      </div>

      <div className="w-full max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="text-center mb-16 animate-on-scroll [animation:animationIn_0.8s_ease-out_0.2s_both]">
          <h2 className="text-3xl md:text-5xl font-display font-light text-white mb-4">
            Comprehensive Services
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto font-light">
            From pre-production planning to final delivery, we handle every aspect
            of your entertainment project.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={service.id}
              className={`group p-8 rounded-3xl glass-card glass-card-hover transition-all duration-300 animate-on-scroll [animation:animationIn_0.8s_ease-out_${
                0.3 + index * 0.1
              }s_both]`}
            >
              <div
                className={`w-12 h-12 rounded-full bg-gradient-to-br ${service.gradient} border border-white/5 flex items-center justify-center mb-6 text-white group-hover:scale-110 transition-transform`}
              >
                <Icon name={service.icon as any} size={24} />
              </div>
              <h3 className="text-lg font-display font-medium text-white mb-3">
                {service.title}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed font-light mb-4">
                {service.description}
              </p>
              <Link
                href="/services"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:gap-3 transition-all"
              >
                Learn More
                <Icon name="ArrowRightIcon" size={16} />
              </Link>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center animate-on-scroll [animation:animationIn_0.8s_ease-out_0.6s_both]">
          <Link
            href="/services"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-red-800 px-8 py-4 rounded-full text-base font-medium text-white transition-all duration-300 glow-primary hover:glow-primary-hover"
          >
            View All Services
            <Icon name="ArrowRightIcon" size={20} />
          </Link>
        </div>
      </div>
    </section>
  );
}